"use client";

import { createContext, useContext, useRef, useState, type ReactNode } from "react";
import ImagePageLightbox from "./ImagePageLightbox";
import LeadButton from "@/component/ui/Lead/LeadButton";
import type { GalleryCategoryId, ImagePageGalleryData } from "./types";

type ImagePageMediaContextValue = {
    openLightbox: (imageId?: number | string, category?: GalleryCategoryId, subcategory?: string) => void;
    activeCategory: GalleryCategoryId;
    activeSubcategory: string;
    setActiveCategory: (id: GalleryCategoryId) => void;
    setActiveSubcategory: (id: string) => void;
};

const ImagePageMediaContext = createContext<ImagePageMediaContextValue>({
    openLightbox: () => {},
    activeCategory: "all",
    activeSubcategory: "all",
    setActiveCategory: () => {},
    setActiveSubcategory: () => {},
});

export function useImagePageMedia() {
    return useContext(ImagePageMediaContext);
}

type ImagePageMediaProps = {
    data: ImagePageGalleryData;
    carId?: string;
    children: ReactNode;
    useSplashPopup?: boolean;
};

export default function ImagePageMedia({ data, carId, children, useSplashPopup = false }: ImagePageMediaProps) {
    const offersLeadRef = useRef<HTMLDivElement>(null);
    const [activeCategory, setActiveCategory] = useState<GalleryCategoryId>("all");
    const [activeSubcategory, setActiveSubcategory] = useState("all");
    const [lightbox, setLightbox] = useState<{
        open: boolean;
        imageId?: number | string;
        category: GalleryCategoryId;
        subcategory?: string;
    }>({
        open: false,
        category: "all",
    });

    const openLightbox = (
        imageId?: number | string,
        category: GalleryCategoryId = "all",
        subcategory?: string,
    ) => {
        setLightbox({ open: true, imageId, category, subcategory });
    };

    return (
        <ImagePageMediaContext.Provider
            value={{
                openLightbox,
                activeCategory,
                activeSubcategory,
                setActiveCategory,
                setActiveSubcategory,
            }}
        >
            {children}
            <ImagePageLightbox
                data={data}
                isOpen={lightbox.open}
                imageId={lightbox.imageId}
                startCategory={lightbox.category}
                startSubcategory={lightbox.subcategory}
                onClose={() => setLightbox((current) => ({ ...current, open: false }))}
                onOffersClick={() => {
                    setLightbox((current) => ({ ...current, open: false }));
                    window.setTimeout(() => {
                        offersLeadRef.current?.querySelector("button")?.click();
                    }, 0);
                }}
            />
            <div ref={offersLeadRef} className="hidden">
                <LeadButton carId={carId} carName={data.carName} btnText={data.offersButtonText} useSplashPopup={useSplashPopup} />
            </div>
        </ImagePageMediaContext.Provider>
    );
}
